import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { apiErrorMessage } from '../sales/shared';
import { errorDetail } from '../ui/stateBlockModel';

/**
 * Small pieces shared by the task / milestone forms (T-46) and the detail
 * pages that host them: the server-error note (DESIGN_SYSTEM.md §11 rule 4:
 * what happened in Hebrew, technical detail only under "פרטים טכניים") and the
 * submit button (spinner inside, label kept, double submit blocked).
 */
export function ServerError({ error, fallback, className = '' }: { error: unknown; fallback: string; className?: string }) {
  const { t } = useTranslation();
  if (!error) return null;
  const main = apiErrorMessage(error, fallback);
  const detail = errorDetail(error);
  return (
    <div role="alert" className={`rounded-lg border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700 ${className}`}>
      <p>{main}</p>
      {detail && detail !== main && (
        <details className="mt-1 text-xs">
          <summary className="cursor-pointer select-none">{t('states.details')}</summary>
          <p className="mt-1 break-words" dir="auto">{detail}</p>
        </details>
      )}
    </div>
  );
}

export function SubmitButton({ busy, children, form, className = '', disabled = false }: {
  busy: boolean;
  children: ReactNode;
  form?: string;
  className?: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="submit"
      form={form}
      className={`btn btn-primary ${className}`}
      disabled={busy || disabled}
      aria-busy={busy || undefined}
    >
      {busy && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
      {children}
    </button>
  );
}

/** "ביטול" + the primary at the end (DESIGN_SYSTEM.md §13 actions), for a dialog footer. */
export function FormFooter({ formId, busy, submitLabel, onCancel, disabled }: {
  formId: string;
  busy: boolean;
  submitLabel: ReactNode;
  onCancel: () => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <>
      <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={busy}>{t('common.cancel')}</button>
      <SubmitButton form={formId} busy={busy} disabled={disabled}>{submitLabel}</SubmitButton>
    </>
  );
}
